import React from "react";
import { Card } from "../../features/ui/card";
import { Skeleton } from "../../features/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../features/ui/table";

interface SalesLoaderProps {
    variant?: "sales-kpi" | "sales-chart" | "performance-bar" | "performance-pie" | "performance-channel" | "performance-category" | "top-product" | "sales-data";
    className?: string;
    itemCount?: number; // Optional: customizable item count
}


const SalesLoader: React.FC<SalesLoaderProps> = ({ variant = "list", className = "" }) => {
    switch (variant) {
        case "sales-kpi":
            return (
                <Card className="bg-white/70 dark:bg-black/70 rounded-xl shadow-xl-soft w-full">
                    <div className={`${className}`} aria-busy="true" role="status">
                        <div className="m-2 mb-4">
                            <div className="space-y-2">
                                <Skeleton className="h-6 w-24 rounded-md bg-secondary" />
                                <Skeleton className="h-3 w-24 mt-2 rounded-md bg-secondary" />
                            </div>
                            <Skeleton className="h-2.5 w-28 mt-2 rounded-md bg-secondary" />
                        </div>
                    </div>
                </Card>
            )
        case "sales-chart":
            return (
                <Card className="bg-white/70 dark:bg-black/70 rounded-xl shadow-xl-soft w-full">
                    <div className={className} aria-busy="true" role="status">
                        <div className="flex flex-row px-6 pt-6 space-x-4">
                            {/* Y-axis bar */}
                            <Skeleton className="h-80 w-6 rounded-lg bg-secondary" />
                            {/* Simulated chart area */}
                            <Skeleton className="h-70 w-full rounded-lg bg-secondary" />
                        </div>
                        {/* Bottom axis labels */}
                        <div className="px-6 pt-4 pb-4">
                            <Skeleton className="h-4 w-full rounded-md bg-secondary" />
                        </div>
                        <div className="flex justify-center space-x-2 mb-4  ">
                            <Skeleton className="h-6 w-14 bg-secondary" />
                            <Skeleton className="h-6 w-14 bg-secondary" />
                        </div>

                    </div>
                </Card>
            )
        case "performance-bar":
            return (
                <div className={className} aria-busy="true" role="status">
                    <div className="flex flex-row px-6 space-x-4">
                        {/* Y-axis bar */}
                        <Skeleton className="h-80 w-6 rounded-lg bg-secondary" />
                        {/* Simulated chart area */}
                        <Skeleton className="h-70 w-full rounded-lg bg-secondary" />
                    </div>
                    {/* Bottom axis labels */}
                    <div className="px-6 pt-4 pb-4">
                        <Skeleton className="h-4 w-full rounded-md bg-secondary" />
                    </div>
                    <div className="flex justify-center space-x-2 mb-4  ">
                        <Skeleton className="h-6 w-14 bg-secondary" />

                    </div>

                </div>
            )
        case "performance-pie":
            return (
                <div className={className} aria-busy="true" role="status">
                    <div className="flex justify-center items-center h-[310px] m-6">
                        <div className="relative">
                            <Skeleton className="w-[250px] h-[250px] rounded-full bg-secondary" />
                        </div>
                    </div>
                </div>
            )
        case "performance-channel":
            return (
                <div className="rounded-md border overflow-hidden">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead className="text-left">Channel</TableHead>
                                <TableHead className="text-right">Revenue</TableHead>
                                <TableHead className="text-right">Orders</TableHead>
                                <TableHead className="text-right">Units</TableHead>
                                <TableHead className="text-right">AOV</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {Array.from({ length: 9 }).map((_, idx) => (
                                <TableRow key={idx}>
                                    <TableCell className="text-left">
                                        <Skeleton className="h-4 w-full rounded bg-secondary" />
                                    </TableCell>
                                    <TableCell className="text-right p-4">
                                        <Skeleton className="h-4 w-20 rounded bg-secondary ml-auto" />
                                    </TableCell>
                                    <TableCell className="text-right p-4">
                                        <Skeleton className="h-4 w-14 rounded bg-secondary ml-auto" />
                                    </TableCell>
                                    <TableCell className="text-right p-4">
                                        <Skeleton className="h-4 w-14 rounded bg-secondary ml-auto" />
                                    </TableCell>
                                    <TableCell className="text-right p-4">
                                        <Skeleton className="h-4 w-12 rounded bg-secondary ml-auto" />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )
        case "performance-category":
            return (
                <div className="rounded-md border overflow-hidden">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead className="text-left">Category</TableHead>
                                <TableHead className="text-right">Revenue</TableHead>
                                <TableHead className="text-right">Orders</TableHead>
                                <TableHead className="text-right">Units</TableHead>
                                <TableHead className="text-right">AOV</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {Array.from({ length: 9 }).map((_, idx) => (
                                <TableRow key={idx}>
                                    <TableCell className="text-left">
                                        <Skeleton className="h-4 w-full rounded bg-secondary" />
                                    </TableCell>
                                    <TableCell className="text-right p-4">
                                        <Skeleton className="h-4 w-20 rounded bg-secondary ml-auto" />
                                    </TableCell>
                                    <TableCell className="text-right p-4">
                                        <Skeleton className="h-4 w-14 rounded bg-secondary ml-auto" />
                                    </TableCell>
                                    <TableCell className="text-right p-4">
                                        <Skeleton className="h-4 w-14 rounded bg-secondary ml-auto" />
                                    </TableCell>
                                    <TableCell className="text-right p-4">
                                        <Skeleton className="h-4 w-12 rounded bg-secondary ml-auto" />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )
        case "top-product":
            return (
                <div className="rounded-md border">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Product</TableHead>
                                <TableHead>SKU</TableHead>
                                <TableHead>Category</TableHead>
                                <TableHead className="text-right">Price</TableHead>
                                <TableHead className="text-right">Sales</TableHead>
                                <TableHead className="text-right">Revenue</TableHead>
                                <TableHead className="text-right">Profit</TableHead>
                                <TableHead className="text-right">Stock</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {Array.from({ length: 10 }).map((_, idx) => (
                                <TableRow key={idx}>
                                    <TableCell>
                                        <Skeleton className="h-4 w-32 rounded bg-secondary" />
                                    </TableCell>
                                    <TableCell>
                                        <Skeleton className="h-4 w-20 rounded bg-secondary" />
                                    </TableCell>
                                    <TableCell>
                                        <Skeleton className="h-4 w-24 rounded bg-secondary" />
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <Skeleton className="h-4 w-16 rounded bg-secondary ml-auto" />
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <Skeleton className="h-4 w-16 rounded bg-secondary ml-auto" />
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <Skeleton className="h-4 w-20 rounded bg-secondary ml-auto" />
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <Skeleton className="h-4 w-20 rounded bg-secondary ml-auto" />
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <Skeleton className="h-4 w-12 rounded bg-secondary ml-auto" />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )
        case "sales-data":
            return (
                <div className="rounded-md border">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead className="text-left">Date</TableHead>
                                <TableHead className="text-left">SKU</TableHead>
                                <TableHead className="text-left">Product</TableHead>
                                <TableHead className="text-left">Category</TableHead>
                                <TableHead className="text-left">Channel</TableHead>
                                <TableHead className="text-left">Qty</TableHead>
                                <TableHead className="text-left">Unit Price</TableHead>
                                <TableHead className="text-left">Total</TableHead>
                                <TableHead className="text-left">Profit</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {Array.from({ length: 10 }).map((_, idx) => (
                                <TableRow key={idx}>
                                    <TableCell className="text-left p-4">
                                        <Skeleton className="h-4 w-20 bg-secondary rounded" />
                                    </TableCell>
                                    <TableCell className="text-left p-4">
                                        <Skeleton className="h-4 w-16 bg-secondary rounded" />
                                    </TableCell>
                                    <TableCell className="text-left p-4">
                                        <Skeleton className="h-4 w-28 bg-secondary rounded" />
                                    </TableCell>
                                    <TableCell className="text-left p-4">
                                        <Skeleton className="h-4 w-24 bg-secondary rounded" />
                                    </TableCell>
                                    <TableCell className="text-left p-4">
                                        <Skeleton className="h-4 w-20 bg-secondary rounded" />
                                    </TableCell>
                                    <TableCell className="text-left p-4">
                                        <Skeleton className="h-4 w-12 bg-secondary rounded" />
                                    </TableCell>
                                    <TableCell className="text-left p-4">
                                        <Skeleton className="h-4 w-16 bg-secondary rounded" />
                                    </TableCell>
                                    <TableCell className="text-left p-4">
                                        <Skeleton className="h-4 w-16 bg-secondary rounded" />
                                    </TableCell>
                                    <TableCell className="text-left p-4">
                                        <Skeleton className="h-4 w-16 bg-secondary rounded" />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )

    }
}

export default SalesLoader